// AddPayrollV2 - synced with marketing site design.
// Shows 3 payroll tiers (Core, Premium, Elite) in low-to-high price order.
// Uses shared pricing config from src/config/pricing.ts as single source of truth.
// Employee count input drives live per-tier totals.

import React, { useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from '../../hooks/useQuery';
import { useTranslation } from "react-i18next";
import type { PayrollTierId } from "../../config/countries";
import MarketingHeader from "../../components/MarketingHeader";
import MarketingFooter from "../../components/MarketingFooter";
import FeaturesSection from "../../components/FeaturesSection";
import { Stepper } from './components/Stepper';
import { TierCard } from './components/TierCard';
import { TIER_META } from './constants';

export default function AddPayrollV2() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const q = useQuery();
  const planId = q.get("plan") || "essentials";
  const billing = q.get("billing") || "monthly";

  const [employeeCount, setEmployeeCount] = useState(5);
  const [selectedTier, setSelectedTier] = useState<PayrollTierId | null>(null);

  const goToCart = (tierId: PayrollTierId) => {
    navigate(`/cart-v2?plan=${planId}&billing=${billing}&payroll=${tierId}&employees=${employeeCount}`);
  };
  const skipPayroll = () => {
    navigate(`/cart-v2?plan=${planId}&billing=${billing}&payroll=none&employees=${employeeCount}`);
  };

  const orderedTiers = useMemo(
    () => (Object.keys(TIER_META) as PayrollTierId[]).sort((a, b) => TIER_META[a].order - TIER_META[b].order),
    []
  );

  return (
    <div className="min-h-screen bg-white font-sans">
      <MarketingHeader />

      <div className="text-center" style={{ background: "#FEF3C7", padding: "10px 32px" }}>
        <span className="text-tiny font-bold" style={{ color: "#92400E", letterSpacing: "0.05em" }}>
          {t("common.checkout.founding")}
        </span>
      </div>

      <div className="text-center" style={{ padding: "32px 32px 20px" }}>
        <Stepper t={t} />
      </div>

      <div className="mx-auto text-center" style={{ maxWidth: 1240, padding: "20px 32px 0" }}>
        <div className="mb-4 text-tiny font-bold text-brand" style={{ letterSpacing: "0.3em" }}>{t("addPayroll.step")}</div>
        <h1 className="font-extrabold text-ink" style={{ fontSize: 48, letterSpacing: "-0.03em", margin: "0 0 16px 0", lineHeight: 1 }}>
          {t("addPayroll.title")}
        </h1>
        <p className="mx-auto text-sub" style={{ fontSize: 17, margin: "0 auto 24px", maxWidth: 700 }}>
          {t("addPayroll.subtitle")}
        </p>
        <button
          onClick={skipPayroll}
          className="cursor-pointer rounded-[10px] border-none bg-ink text-white font-semibold text-small font-sans"
          style={{ padding: "10px 16px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
        >{t("addPayroll.continueWithout")}</button>
      </div>

      <div className="mx-auto" style={{ maxWidth: 1240, padding: "40px 32px" }}>
        <div className="grid items-start gap-6" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
          {orderedTiers.map((tierId) => (
            <TierCard key={tierId} tierId={tierId} employeeCount={employeeCount} planId={planId} billing={billing} onSelect={goToCart} isSelected={selectedTier === tierId} onCardClick={setSelectedTier} />
          ))}
        </div>
      </div>

      <FeaturesSection />

      <MarketingFooter />
    </div>
  );
}
